import React from 'react';

const CookiePolicy = () => {
  return (
    <div style={styles.page}>
      <div style={styles.content}>
        <h1>Cookie &amp; Storage Policy</h1>
        <p style={styles.updated}>Last updated: July 2026</p>

        <h2>Cookies</h2>
        <p>
          This website does <strong>not</strong> set any HTTP cookies. You will not find any cookies
          from this site in your browser's cookie storage.
        </p>

        <h2>Local storage</h2>
        <p>
          We use your browser's <strong>local storage</strong> (not cookies) to make the site work
          correctly. Local storage is similar to cookies but is never sent to our servers with each
          request — it stays in your browser. The table below lists everything we store and why.
        </p>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Key</th>
                <th style={styles.th}>Contents</th>
                <th style={styles.th}>Purpose</th>
                <th style={styles.th}>Legal basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.td}><code>persist:preferences</code></td>
                <td style={styles.td}>Your chosen language</td>
                <td style={styles.td}>Remembers your language preference across page loads</td>
                <td style={styles.td}>Strictly necessary</td>
              </tr>
              <tr style={styles.trAlt}>
                <td style={styles.td}><code>persist:domainData</code></td>
                <td style={styles.td}>Site configuration and page list</td>
                <td style={styles.td}>Caches site structure fetched from our API to speed up navigation</td>
                <td style={styles.td}>Strictly necessary</td>
              </tr>
              <tr>
                <td style={styles.td}><code>persist:dynamicData</code></td>
                <td style={styles.td}>Page content blocks</td>
                <td style={styles.td}>Caches dynamic content from our API to avoid repeated network requests</td>
                <td style={styles.td}>Strictly necessary</td>
              </tr>
              <tr style={styles.trAlt}>
                <td style={styles.td}><code>persist:store</code></td>
                <td style={styles.td}>Product catalogue data</td>
                <td style={styles.td}>Caches store/product information for the shopping section</td>
                <td style={styles.td}>Strictly necessary</td>
              </tr>
              <tr>
                <td style={styles.td}><code>persist:rssData</code></td>
                <td style={styles.td}>RSS feed content and timestamps</td>
                <td style={styles.td}>Caches news/blog feed so it loads instantly on repeat visits</td>
                <td style={styles.td}>Strictly necessary</td>
              </tr>
              <tr style={styles.trAlt}>
                <td style={styles.td}><code>gdpr:consent</code></td>
                <td style={styles.td}>Timestamp and version of consent acknowledgement</td>
                <td style={styles.td}>Records that you have seen and dismissed this notice so it does not reappear</td>
                <td style={styles.td}>Strictly necessary</td>
              </tr>
              <tr>
                <td style={styles.td}><code>aw_theme</code></td>
                <td style={styles.td}>Your chosen site theme</td>
                <td style={styles.td}>Remembers which visual theme you selected in site settings</td>
                <td style={styles.td}>Functional (your choice)</td>
              </tr>
              <tr style={styles.trAlt}>
                <td style={styles.td}><code>aw_scheme</code></td>
                <td style={styles.td}>Light / dark / auto appearance setting</td>
                <td style={styles.td}>Remembers whether you prefer light mode, dark mode, or following your device setting</td>
                <td style={styles.td}>Functional (your choice)</td>
              </tr>
              <tr>
                <td style={styles.td}><code>aw_accent</code></td>
                <td style={styles.td}>Your chosen accent colour</td>
                <td style={styles.td}>Remembers the accent colour you picked in site settings</td>
                <td style={styles.td}>Functional (your choice)</td>
              </tr>
              <tr style={styles.trAlt}>
                <td style={styles.td}><code>aw_quickmenu_hidden</code></td>
                <td style={styles.td}>Quick-menu visibility flag</td>
                <td style={styles.td}>Remembers that you hid the quick menu on phones and tablets</td>
                <td style={styles.td}>Functional (your choice)</td>
              </tr>
              <tr style={styles.trAlt}>
                <td style={styles.td}><code>aw_zoom</code></td>
                <td style={styles.td}>Your page size (zoom) choice</td>
                <td style={styles.td}>Remembers the page size you set with the A&minus; / A+ buttons</td>
                <td style={styles.td}>Functional (your choice)</td>
              </tr>
              <tr>
                <td style={styles.td}><code>aw_viator_ok</code></td>
                <td style={styles.td}>Attractions widget consent flag</td>
                <td style={styles.td}>Remembers that you chose to load the Viator tours &amp; attractions listing</td>
                <td style={styles.td}>Consent (your choice)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>No advertising or tracking</h2>
        <p>
          We do not use Google Analytics, Meta Pixel, Google Tag Manager, Hotjar, or any other
          analytics or advertising platform. None of the data listed above is shared with advertisers
          or used to build a profile of you.
        </p>

        <h2>Third-party content</h2>
        <p>
          Some pages show a <strong>Viator</strong> (TripAdvisor company) attractions banner — a
          simple image with a link. It sets <strong>no cookies</strong>; your browser only fetches
          the image from Viator's content network, and nothing about you is stored.
        </p>
        <p>
          Some pages also offer a fuller tours-and-attractions listing from Viator. It loads only if
          you press &ldquo;Show attractions&rdquo;. Once loaded, Viator's own script runs in your
          browser and <strong>may set Viator cookies</strong> for functionality and affiliate
          attribution — these are governed by the{' '}
          <a href="https://www.viator.com/support/privacyPolicy" target="_blank" rel="noopener noreferrer">Viator privacy policy</a>,
          not by us. If you never press the button, no Viator cookies are set and no connection to
          Viator's application is made.
        </p>

        <h2>Fonts</h2>
        <p>
          The Montserrat typeface used on this site is served from our own servers. No request is
          made to Google Fonts or any third-party font provider, so your IP address is not shared
          with Google in connection with loading this site's typography.
        </p>

        <h2>How to clear local storage</h2>
        <p>
          You can clear local storage at any time through your browser's developer tools or settings:
        </p>
        <ul>
          <li><strong>Chrome / Edge:</strong> DevTools → Application → Local Storage → right-click → Clear</li>
          <li><strong>Firefox:</strong> DevTools → Storage → Local Storage → right-click → Delete All</li>
          <li><strong>Safari:</strong> Develop → Show Web Inspector → Storage → Local Storage → Clear</li>
        </ul>
        <p>
          Clearing local storage will reset your language preference, your theme, appearance and
          accent choices, and remove cached content; the site will re-fetch everything on your
          next visit.
        </p>

        <h2>More information</h2>
        <p>
          See our full <a href="/privacy-policy">Privacy Policy</a> or visit{' '}
          <a href="https://www.any-web.uk/legal_privacy.aspx">any-web.uk/legal_privacy</a> for
          further details.
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: '40px 16px',
    background: '#fff',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
    fontFamily: 'Montserrat, sans-serif',
    color: '#1a1b1f',
    lineHeight: '1.7',
  },
  updated: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '32px',
  },
  tableWrapper: {
    overflowX: 'auto',
    marginBottom: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    background: '#1a1b1f',
    color: '#fff',
    padding: '10px 14px',
    textAlign: 'left',
    fontWeight: 600,
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'top',
  },
  trAlt: {
    background: '#f9f9f9',
  },
};

export default CookiePolicy;
